/**
 * Calm Mode Context
 * 2026 Ethical Design Standards
 *
 * Provides a reduced-stimulation interface option for users who prefer:
 * - No animations
 * - Muted colors
 * - Reduced notifications
 * - Spacious UI density
 *
 * The setting persists in localStorage and respects system preferences.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

// ============================================
// TYPES
// ============================================

interface CalmModeSettings {
  /** Main toggle for calm mode */
  enabled: boolean;
  /** Override system preference for reduced motion */
  overrideSystemPreference: boolean;
  /** Notification level: 'all' | 'important' | 'critical' */
  notificationLevel: 'all' | 'important' | 'critical';
  /** Color saturation level (0-100) */
  colorSaturation: number;
}

interface CalmModeContextValue {
  /** Whether calm mode is currently active */
  isCalm: boolean;
  /** Full settings object */
  settings: CalmModeSettings;
  /** Toggle calm mode on/off */
  toggleCalmMode: () => void;
  /** Update specific settings */
  updateSettings: (updates: Partial<CalmModeSettings>) => void;
  /** Reset to defaults */
  resetSettings: () => void;
  /** Whether system prefers reduced motion */
  systemPrefersReducedMotion: boolean;
}

// ============================================
// DEFAULTS
// ============================================

const DEFAULT_SETTINGS: CalmModeSettings = {
  enabled: false,
  overrideSystemPreference: false,
  notificationLevel: 'all',
  colorSaturation: 100,
};

const CALM_MODE_SETTINGS: Partial<CalmModeSettings> = {
  colorSaturation: 60,
  notificationLevel: 'critical',
};

const STORAGE_KEY = 'omnitower-calm-mode';

// ============================================
// CONTEXT
// ============================================

const CalmModeContext = createContext<CalmModeContextValue | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface CalmModeProviderProps {
  children: React.ReactNode;
}

export function CalmModeProvider({ children }: CalmModeProviderProps) {
  const [settings, setSettings] = useState<CalmModeSettings>(DEFAULT_SETTINGS);
  const [systemPrefersReducedMotion, setSystemPrefersReducedMotion] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[CalmMode] Failed to load settings:', error);
    }
  }, []);

  // Detect system preference for reduced motion
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setSystemPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => {
      setSystemPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[CalmMode] Failed to save settings:', error);
    }
  }, [settings]);

  // Apply calm mode to document
  useEffect(() => {
    const isCalm = settings.enabled ||
      (!settings.overrideSystemPreference && systemPrefersReducedMotion);

    // Set data attribute on document
    document.documentElement.setAttribute('data-calm-mode', String(isCalm));

    // Set CSS custom properties
    const saturation = isCalm ? CALM_MODE_SETTINGS.colorSaturation : settings.colorSaturation;
    document.documentElement.style.setProperty(
      '--color-saturation',
      `${saturation}%`
    );

    // Set animation speed
    document.documentElement.style.setProperty(
      '--animation-speed',
      isCalm ? '0ms' : '300ms'
    );

    // Set transition speed
    document.documentElement.style.setProperty(
      '--transition-speed',
      isCalm ? '0ms' : '200ms'
    );

    return () => {
      document.documentElement.removeAttribute('data-calm-mode');
      document.documentElement.style.removeProperty('--color-saturation');
      document.documentElement.style.removeProperty('--animation-speed');
      document.documentElement.style.removeProperty('--transition-speed');
    };
  }, [settings, systemPrefersReducedMotion]);

  // Computed values
  const isCalm = useMemo(() => {
    return settings.enabled ||
      (!settings.overrideSystemPreference && systemPrefersReducedMotion);
  }, [settings.enabled, settings.overrideSystemPreference, systemPrefersReducedMotion]);

  // Actions
  const toggleCalmMode = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      enabled: !prev.enabled,
      // When enabling, apply calm defaults
      ...(prev.enabled ? {} : CALM_MODE_SETTINGS),
    }));
  }, []);

  const updateSettings = useCallback((updates: Partial<CalmModeSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // Context value
  const value = useMemo<CalmModeContextValue>(
    () => ({
      isCalm,
      settings,
      toggleCalmMode,
      updateSettings,
      resetSettings,
      systemPrefersReducedMotion,
    }),
    [isCalm, settings, toggleCalmMode, updateSettings, resetSettings, systemPrefersReducedMotion]
  );

  return (
    <CalmModeContext.Provider value={value}>
      {children}
    </CalmModeContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

/**
 * Hook for accessing calm mode state and controls
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isCalm, toggleCalmMode, settings } = useCalmMode();
 *
 *   return (
 *     <div className={isCalm ? 'calm-ui' : ''}>
 *       <button onClick={toggleCalmMode}>
 *         {isCalm ? 'Disable' : 'Enable'} Calm Mode
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCalmMode(): CalmModeContextValue {
  const context = useContext(CalmModeContext);

  if (context === undefined) {
    throw new Error('useCalmMode must be used within a CalmModeProvider');
  }

  return context;
}

// ============================================
// TOGGLE COMPONENT
// ============================================

interface CalmModeToggleProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show label */
  showLabel?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Standalone toggle component for calm mode
 */
export function CalmModeToggle({
  size = 'md',
  showLabel = true,
  className = '',
}: CalmModeToggleProps) {
  const { isCalm, toggleCalmMode, systemPrefersReducedMotion } = useCalmMode();

  const sizeClasses = {
    sm: 'w-8 h-4',
    md: 'w-10 h-5',
    lg: 'w-12 h-6',
  };

  const dotSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const translateClasses = {
    sm: 'translate-x-4',
    md: 'translate-x-5',
    lg: 'translate-x-6',
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <span className="text-sm text-gray-600">
          Modo Calma
          {systemPrefersReducedMotion && !isCalm && (
            <span className="ml-1 text-xs text-gray-600">(Sistema)</span>
          )}
        </span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={isCalm}
        aria-label="Activar modo calma"
        onClick={toggleCalmMode}
        className={`
          relative inline-flex shrink-0 cursor-pointer rounded-full
          transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2
          ${sizeClasses[size]}
          ${isCalm ? 'bg-primary' : 'bg-gray-200'}
        `}
      >
        <span
          className={`
            pointer-events-none inline-block rounded-full bg-white shadow-sm
            transform transition-transform duration-200 ease-in-out
            ${dotSizeClasses[size]}
            ${isCalm ? translateClasses[size] : 'translate-x-0.5'}
          `}
          style={{ marginTop: '0.125rem' }}
        />
      </button>
    </div>
  );
}

export default CalmModeContext;
